import axios, { AxiosHeaders, type AxiosInstance } from 'axios'
import { v4 as uuidv4 } from 'uuid'

import { ErrorHandlerServiceFactory } from '@/infrastructure/http/factories/ErrorHandlerServiceFactory'
import type { HttpErrorHandlerService } from '@/infrastructure/http/services/HttpErrorHandlerService'
import store from '@/store'
import { reportHttpRequestFailure } from '@/telemetry/httpRequestFailure'
import { getToken } from '@/utils/auth'

export function requestServiceFactory(
  baseURL: string = import.meta.env.VITE_BASE_API,
  errorHandlerService: HttpErrorHandlerService
): AxiosInstance {
  // create an axios instance
  const service = axios.create({
    baseURL,
    paramsSerializer: params => {
      const searchParams = new URLSearchParams()
      Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          // Serialize arrays as multiple params with the same key (without brackets)
          value.forEach(item => searchParams.append(key, String(item)))
        } else if (value !== undefined && value !== null) {
          searchParams.append(key, String(value))
        }
      })
      return searchParams.toString()
    }
  })

  const excludeAuthorization = ['/refresh']

  // request interceptor
  service.interceptors.request.use(
    config => {
      const url = config.url
      if (url === undefined) return config

      config.headers = AxiosHeaders.from(config.headers)

      const isExcluded = excludeAuthorization.includes(url)

      if (store.getters.token && !isExcluded) {
        config.headers.set('Authorization', getToken())
      }
      config.headers.set('traceId', uuidv4())

      return config
    },
    error => {
      Promise.reject(error)
    }
  )

  service.interceptors.response.use(
    response => response,
    async error => {
      reportHttpRequestFailure(error)

      try {
        const result = await errorHandlerService.handleError(error)

        if (result.customAction) {
          await result.customAction()
        }

        if (result.shouldReject) {
          return Promise.reject(error)
        } else {
          // Some handlers might want to resolve instead of reject (e.g., 409 conflicts)
          return Promise.resolve(error.response)
        }
      } catch (errorHandlerError) {
        console.error('Error handling error:', errorHandlerError)
        return Promise.reject(error)
      }
    }
  )

  return service
}

export default requestServiceFactory(import.meta.env.VITE_BASE_API, ErrorHandlerServiceFactory.createDefault())
